import { ACTION_STATUSES } from 'shared/consts';
import { Container, StyledPaymentMethodsManager, ErrorWrapper, ErrorLabel } from './styles';
import { Order, UIEnablements } from 'store/cart/types';
import React, { useMemo, useState } from 'react';
import { SavedPaymentMethod } from 'store/payment/types';
import { useIntl } from 'react-intl';
import { AddCard } from '../add-card';
import { usePaymentLabels } from './hooks/use-payment-labels.hook';
import { usePaymentMethodsManager } from './hooks/use-payment-methods-manager.hook';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getApplicablePaymentMethods } from 'utils/getApplicablePaymentMethods';
import { usePriceQuery } from 'data/queries/getPriceQuery.hook';
import { selectResource } from 'store/resource/selectors';
import { useSelector } from 'react-redux';

export interface PaymentMethodsProps {
  cart: Order;
  onCancelAddCardClick: VoidFunction;
  formTitle?: string;
  enabledPaymentMethods: UIEnablements;
  paymentMethods: Array<SavedPaymentMethod>;
  selectedPaymentMethodId?: string;
  onRemovePaymentMethod: VoidFunction;
  onAddNewCard: VoidFunction;
  submittedPaymentMethodId?: number;
  currentPaymentError?: boolean;
  cartId: string;
  total?: number;
  isSwipeModalContent?: boolean;
  showAddCardForm: boolean;
  isAddCardButtonDisabled: boolean;
  isNativePaymentError: boolean;
  setIsAddCardButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}

export const PaymentMethods = ({
  formTitle,
  paymentMethods,
  selectedPaymentMethodId,
  onRemovePaymentMethod,
  onAddNewCard,
  submittedPaymentMethodId,
  currentPaymentError,
  cartId,
  total,
  isSwipeModalContent = false,
  showAddCardForm,
  cart,
  onCancelAddCardClick,
  isAddCardButtonDisabled,
  setIsAddCardButtonDisabled,
  isNativePaymentError,
}: PaymentMethodsProps) => {
  const intl = useIntl();
  const [errorPaymentId, setErrorPaymentId] = useState<number>(undefined);
  const { enableHqoMiniappApplePay } = useFlags();
  const isApplePaySupported: boolean = enableHqoMiniappApplePay && navigator.userAgent.includes('iPhone');
  const resource = useSelector(selectResource);
  const priceQuery = usePriceQuery({
    resourceId: resource?.id,
    timezone: resource?.timezone,
  });
  const creditTotalFromPrice = priceQuery?.data?.price?.credit_total;
  const creditTotalCeil = creditTotalFromPrice != null ? Math.ceil(creditTotalFromPrice) : undefined;

  const creditPrice = creditTotalCeil;
  const availableCredit =
    priceQuery?.data?.price?.available_credit != null ? Math.ceil(priceQuery.data.price.available_credit) : undefined;

  const filteredPaymentMethods = useMemo(
    () => getApplicablePaymentMethods(paymentMethods, cart),
    [paymentMethods, cart],
  );

  const { buildSavedPaymentLabels } = usePaymentLabels({
    paymentMethods: filteredPaymentMethods,
    errorPaymentId,
    total,
    isApplePaySupported,
    creditPrice,
    availableCredit,
    creditCycle: priceQuery?.data?.price?.credit_cycle,
    creditsAssignedUntil: priceQuery?.data?.price?.credits_assigned_until,
  });
  const {
    onPressHandler,
    updatePaymentMethodStatus,
    savePaymentMethodStatus,
    getPaymentMethodStatus,
    deletePaymentMethodsStatus,
  } = usePaymentMethodsManager({
    setErrorPaymentId,
    submittedPaymentMethodId,
    currentPaymentError,
    cartId,
  });
  const isRadioFormDisabled = useMemo<boolean>(
    () =>
      // Disable the form only while a request is actually in flight. Using `!== FULFILLED`
      // here meant a single REJECTED default-persist would lock the form forever, leaving
      // the user unable to switch payment methods.
      updatePaymentMethodStatus === ACTION_STATUSES.PENDING ||
      getPaymentMethodStatus === ACTION_STATUSES.PENDING ||
      deletePaymentMethodsStatus === ACTION_STATUSES.PENDING ||
      savePaymentMethodStatus === ACTION_STATUSES.PENDING ||
      showAddCardForm,
    [
      updatePaymentMethodStatus,
      getPaymentMethodStatus,
      deletePaymentMethodsStatus,
      savePaymentMethodStatus,
      showAddCardForm,
    ],
  );
  const labels = useMemo(
    () => ({
      title: formTitle,
      invoiceText: intl.formatMessage({ id: 'invoice_text' }),
      addCardButtonText: intl.formatMessage({
        id: 'add_new_card',
      }),
    }),
    [intl, formTitle],
  );
  const isPaymentErrorVisible = useMemo<boolean>(
    () => (!!errorPaymentId || isNativePaymentError || currentPaymentError) && isApplePaySupported,
    [errorPaymentId, isNativePaymentError, currentPaymentError, isApplePaySupported],
  );

  return (
    <Container data-testid="edit-payment" isSwipeModalContent={isSwipeModalContent}>
      <StyledPaymentMethodsManager
        paymentMethods={filteredPaymentMethods}
        onOptionClick={onPressHandler}
        onRemovePaymentMethod={onRemovePaymentMethod}
        selectedPaymentMethodId={selectedPaymentMethodId}
        onAddNewCard={onAddNewCard}
        options={buildSavedPaymentLabels()}
        isRadioFormDisabled={isRadioFormDisabled}
        labels={labels}
        isAddCardButtonDisabled={isAddCardButtonDisabled}
      />
      {isPaymentErrorVisible && (
        <ErrorWrapper>
          <ErrorLabel>{intl.formatMessage({ id: 'payment_error' })}</ErrorLabel>
        </ErrorWrapper>
      )}
      {showAddCardForm && (
        <AddCard
          onCancel={onCancelAddCardClick}
          spreedlyEnvKey={cart.ui_enablements.spreedly_env_token}
          cartId={cartId}
          setIsAddCardButtonDisabled={setIsAddCardButtonDisabled}
        />
      )}
    </Container>
  );
};
